import { useMemo } from 'preact/hooks'
import { useLookups } from '@/hooks/useLookups'
import { eventZoneInstant, namedAbbreviation, shortOffset } from '@/utils/timeZones'

export function useTimeZones() {
  const { getTimeZones } = useLookups()

  // Memoize the timeZones call to prevent triggering fetchIndividualLookup on every render
  const timeZones = useMemo(() => getTimeZones() || [], [getTimeZones])

  // Helper to get the timezone abbreviation an event's own zone goes by at that event's time.
  //
  // A zone has two abbreviations — standard and daylight — so the date is what decides between
  // them (ORB-872: a July event in Europe/London was labelled GMT rather than BST). The lookup
  // table carries one static value per zone and cannot answer this, so the date is required. `at`
  // is the event's start or end exactly as the API sent it, which eventZoneInstant knows how to read.
  const getAbbreviation = useMemo(() => {
    return (ianaId: string | null | undefined, at: string | null | undefined): string => {
      if (!ianaId || !at) return ''
      const instant = eventZoneInstant(at, ianaId)
      if (!instant) return ''

      const named = namedAbbreviation(ianaId, instant)
      if (named) return named

      // Zones Intl only ever offsets (Asia/Singapore, Australia/Sydney). The lookup's static
      // abbreviation is safe where the zone has no daylight variant to get wrong, and where it does
      // the offset stays — less familiar than "AEDT", but it is not wrong for half the year.
      const tz = timeZones.find(t => t.Id === ianaId)
      if (tz?.Abbreviation && !tz.SupportsDaylightSavingTime) return tz.Abbreviation
      return shortOffset(ianaId, instant)
    }
  }, [timeZones])

  // Helper to get full timezone display name by IANA ID
  const getDisplayName = useMemo(() => {
    return (ianaId: string | null | undefined): string => {
      if (!ianaId || !timeZones) return ''
      const tz = timeZones.find(t => t.Id === ianaId)
      if (!tz?.DisplayName) return ''

      // Strip out UTC offset portion: "(UTC-05:00) Eastern Time" -> "Eastern Time"
      const displayName = tz.DisplayName.replace(/^\(UTC[^)]*\)\s*/, '')
      return displayName
    }
  }, [timeZones])

  return {
    timeZones,
    getAbbreviation,
    getDisplayName,
  }
}
